import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { BASE_URL } from "../utilities/BASE_URL";



export const loginUser = createAsyncThunk(
    "auth/loginUser",
    async ({ email, password }, { rejectWithValue }) => {
      try {
        const response = await fetch(`${BASE_URL}/autha/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
  
        // A 429 (or a proxy error page) may not carry JSON; without this guard the parse
        // failure replaces the server's reason with "Unexpected token ...".
        const data = await response.json().catch(() => ({}));
  
        if (!response.ok) {
          throw new Error(data.error || "Invalid login credentials");
        }
  
        localStorage.setItem("token", data.token); // ✅ Store token in localStorage
        return data.token; // ✅ Return the token
  
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

// "Sign in with Google": the GIS button hands us a Google-signed ID token (credential);
// the backend verifies it, creates the account on first sign-in, and returns OUR JWT —
// same token shape as loginUser, so everything downstream (localStorage, socket) matches.
export const googleLoginUser = createAsyncThunk(
    "auth/googleLoginUser",
    async (credential, { rejectWithValue }) => {
      try {
        const response = await fetch(`${BASE_URL}/autha/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credential }),
        });

        // A 429 (or a proxy error page) may not carry JSON; without this guard the parse
        // failure replaces the server's reason with "Unexpected token ...".
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Google sign-in failed");
        }

        localStorage.setItem("token", data.token);
        return data.token;

      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

  const authSlice = createSlice({
    name: "auth",
    initialState: {
      token: localStorage.getItem("token") || null,
      loading: false,
      error: null,
    },
    reducers: {
      logout: (state) => {
        state.token = null;
        localStorage.removeItem("token"); // ✅ Remove token on logout
      },
      setToken: (state, action) => {
        state.token = action.payload;
        localStorage.setItem("token", action.payload);
      },
      // auth.error otherwise survives until the NEXT login/Google attempt starts, so a
      // failure from one auth page would render unprompted on the other one.
      clearError: (state) => {
        state.error = null;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(loginUser.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(loginUser.fulfilled, (state, action) => {
          state.loading = false;
          state.token = action.payload;
        })
        .addCase(loginUser.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        })
        // Google sign-in mirrors the email/password login lifecycle exactly.
        .addCase(googleLoginUser.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(googleLoginUser.fulfilled, (state, action) => {
          state.loading = false;
          state.token = action.payload;
        })
        .addCase(googleLoginUser.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        });
    },
  });
  
  export const { logout, setToken, clearError } = authSlice.actions;
  export default authSlice.reducer;
